import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import Body from "src/components/common/Body";
import Button from "src/components/common/Button";
import IconButton from "src/components/common/IconButton";
import AlarmButton from "src/components/common/AlarmButton";
import SVG from "src/utils/SVG";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "src/store/actions/action";
import cn from "classnames";
import { format, subMonths } from "date-fns";
import { useAsListQuery } from "src/api/asAPI";
import { useSelectGroupQuery } from "src/api/userAPI";
import { getCookie } from "src/utils/cookieUtil";

// 그룹채널 타입 정의
interface GroupChannel {
  group_name: string;
  group_part_idx: number | string;
  group_purpose?: string;
}

// 브랜드 코드를 브랜드명으로 변환하는 함수
const changeBrandCode = (code: string | undefined) => {
  if (!code) return "-";

  const upperCode = code.toUpperCase();

  if (upperCode.startsWith("ST")) {
    return "Sergio Tacchini";
  } else if (upperCode.startsWith("M")) {
    return "MLB";
  } else if (upperCode.startsWith("X")) {
    return "Discovery";
  } else if (upperCode.startsWith("I")) {
    return "MLB KIDS";
  } else if (upperCode.startsWith("A")) {
    return "STRETCH ANGELS";
  } else if (upperCode.startsWith("V")) {
    return "DUVETICA";
  } else {
    return "SUPRA";
  }
};

// 임시 데이터 타입
interface RepairItem {
  id: number;
  brand: string;
  storeType: string;
  storeName: string;
  requestContent: string;
  requestDate: string;
  companyAssignedDate: string;
  completeDate: string;
  companyName: string;
  status: string;
  amount: number;
}

// API 쿼리 파라미터 타입
interface AsListQueryParams {
  "filter.insert_date": string;
  sortBy: string;
  page: number;
  limit: number;
  "filter.store_user.brand"?: string;
  "filter.store_type"?: string;
  "filter.search_progress"?: string;
  "filter.group_part_idx"?: string;
}

// API 응답 타입
interface AsListApiResponse {
  data: {
    data: Array<{
      as_idx?: number;
      brand?: string;
      store_id?: string;
      store_type?: string;
      store_type_name?: string;
      store_name?: string;
      contents?: string;
      insert_date?: string;
      visit_date?: string;
      company_assigned_date?: string;
      completion_date?: string;
      company_name?: string;
      delete_yn?: string;
      progress_name?: string;
      prog_name?: string;
      total_price?: number;
    }>;
    meta: {
      totalItems?: number;
      total?: number;
    };
  };
}

// 임시 데이터
const mockData: RepairItem[] = [
  {
    id: 1,
    brand: "MLB",
    storeType: "백화점",
    storeName: "강남점",
    requestContent: "천장 누수 보수",
    requestDate: "2024-11-20",
    companyAssignedDate: "2024-11-21",
    completeDate: "2024-11-24",
    companyName: "㈜건설",
    status: "완료",
    amount: 1500000,
  },
  {
    id: 2,
    brand: "MLB KIDS",
    storeType: "대리점",
    storeName: "홍대점",
    requestContent: "바닥 타일 교체",
    requestDate: "2024-11-19",
    companyAssignedDate: "2024-11-20",
    completeDate: "2024-11-23",
    companyName: "㈜인테리어",
    status: "진행중",
    amount: 2300000,
  },
  {
    id: 3,
    brand: "Discovery",
    storeType: "직영점",
    storeName: "명동점",
    requestContent: "조명 교체",
    requestDate: "2024-11-18",
    companyAssignedDate: "2024-11-19",
    completeDate: "2024-11-22",
    companyName: "㈜전기",
    status: "완료",
    amount: 800000,
  },
  {
    id: 4,
    brand: "DUVETICA",
    storeType: "면세점",
    storeName: "인천공항점",
    requestContent: "간판 교체",
    requestDate: "2024-11-17",
    companyAssignedDate: "2024-11-18",
    completeDate: "2024-11-25",
    companyName: "㈜사인",
    status: "대기",
    amount: 3500000,
  },
  {
    id: 5,
    brand: "MLB",
    storeType: "백화점",
    storeName: "잠실점",
    requestContent: "에어컨 수리",
    requestDate: "2024-11-16",
    companyAssignedDate: "2024-11-17",
    completeDate: "2024-11-20",
    companyName: "㈜설비",
    status: "완료",
    amount: 450000,
  },
];

const RepairList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);
  const [hasAlarm, setHasAlarm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // 날짜 기본값을 메모이제이션하여 한 번만 계산
  const defaultDates = useMemo(() => {
    const today = new Date();
    const oneMonthAgo = subMonths(today, 1);
    return {
      start: format(oneMonthAgo, "yyyy-MM-dd"),
      end: format(today, "yyyy-MM-dd"),
    };
  }, []);

  // URL을 단일 진실 공급원으로 사용 (상태 대신 URL에서 직접 읽기)
  const selectedBrand = searchParams.get("brand") || "";
  const selectedStoreType = searchParams.get("storeType") || "";
  const selectedStatus = searchParams.get("status") || "";
  const startDate = searchParams.get("startDate") || defaultDates.start;
  const endDate = searchParams.get("endDate") || defaultDates.end;
  const currentPage = Number(searchParams.get("page")) || 1;
  const itemsPerPage = 20;

  // 그룹채널 필터 (URL에서 읽기)
  const selectedGroupChannel = searchParams.get("groupChannel") || "";

  // 로컬 상태 (필터 입력용)
  const [brandInput, setBrandInput] = useState(selectedBrand);
  const [storeTypeInput, setStoreTypeInput] = useState(selectedStoreType);
  const [statusInput, setStatusInput] = useState(selectedStatus);
  const [startDateInput, setStartDateInput] = useState(startDate);
  const [endDateInput, setEndDateInput] = useState(endDate);
  const [groupChannelInput, setGroupChannelInput] = useState(selectedGroupChannel);

  // 그룹채널 목록 조회 API
  const groupChannelAPI = useSelectGroupQuery({ userId: userInfo.user_id, type: "S" });

  // 그룹채널 목록 (group_purpose가 비어있는 것들만 필터링)
  const groupChannelList = useMemo(() => {
    if (!groupChannelAPI.isSuccess || !groupChannelAPI.data) return [];
    return groupChannelAPI.data.filter((group: GroupChannel) => !group.group_purpose?.trim());
  }, [groupChannelAPI.isSuccess, groupChannelAPI.data]);

  // URL 파라미터 변경 시 입력 상태 동기화
  useEffect(() => {
    setBrandInput(selectedBrand);
    setStoreTypeInput(selectedStoreType);
    setStatusInput(selectedStatus);
    setStartDateInput(startDate);
    setEndDateInput(endDate);
    setGroupChannelInput(selectedGroupChannel);
  }, [selectedBrand, selectedStoreType, selectedStatus, startDate, endDate, selectedGroupChannel]);

  // URL 쿼리 파라미터 업데이트 (각 필드별로 적절한 기본값과 비교)
  const updateSearchParams = (updates: Record<string, string | number>) => {
    const newParams = new URLSearchParams();

    Object.entries(updates).forEach(([key, value]) => {
      const valueStr = String(value);
      // 각 필드별로 적절한 기본값과 비교
      const shouldDelete =
        !value ||
        value === "" ||
        (key === "startDate" && valueStr === defaultDates.start) ||
        (key === "endDate" && valueStr === defaultDates.end) ||
        (key === "page" && value === 1);

      if (!shouldDelete) {
        newParams.set(key, valueStr);
      }
    });

    setSearchParams(newParams, { replace: true });
  };

  // API 호출을 위한 쿼리 파라미터 설정
  const queryParams: AsListQueryParams = {
    "filter.insert_date": `$btw:${startDate},${endDate}`,
    sortBy: "insert_date:DESC",
    page: currentPage,
    limit: itemsPerPage,
  };

  // 필터 파라미터 추가
  if (selectedBrand) {
    queryParams["filter.store_user.brand"] = `$in:${selectedBrand}`;
  }
  if (selectedStoreType) {
    queryParams["filter.store_type"] = `$in:${selectedStoreType}`;
  }
  if (selectedStatus) {
    queryParams["filter.search_progress"] = `$in:${selectedStatus}`;
  }
  if (selectedGroupChannel) {
    queryParams["filter.group_part_idx"] = `$in:${selectedGroupChannel}`;
  }

  // API 호출
  const { data: apiData, isLoading, error } = useAsListQuery(queryParams) as {
    data: AsListApiResponse | undefined;
    isLoading: boolean;
    error: unknown;
  };

  if (error) {
    console.error("API Error:", error);
  }

  // API 데이터를 RepairItem 형식으로 매핑
  // API 응답 구조: apiData.data.data (배열)
  const rawData = apiData?.data?.data || [];
  const totalItems = apiData?.data?.meta?.totalItems || apiData?.data?.meta?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const repairData: RepairItem[] = rawData.map((item, index: number) => ({
    id: item.as_idx || index,
    brand: changeBrandCode(item.store_id),
    storeType: item.store_type || item.store_type_name || "-",
    storeName: item.store_name || "-",
    requestContent: item.contents || "-",
    requestDate: item.insert_date ? format(new Date(item.insert_date), "yyyy-MM-dd") : "-",
    companyAssignedDate: item.company_assigned_date ? format(new Date(item.company_assigned_date), "yyyy-MM-dd") : "-",
    completeDate: item.completion_date ? format(new Date(item.completion_date), "yyyy-MM-dd") : "-",
    companyName: item.company_name || "-",
    status: item.delete_yn === "Y" ? "취소" : (item.progress_name || item.prog_name || "대기"),
    amount: item.total_price || 0,
  }));

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 전체 선택/해제
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedItems(repairData.map((item) => String(item.id)));
    } else {
      setSelectedItems([]);
    }
  };

  // 개별 선택
  const handleSelectItem = (id: number) => {
    const idStr = String(id);
    if (selectedItems.includes(idStr)) {
      setSelectedItems(selectedItems.filter((item) => item !== idStr));
    } else {
      setSelectedItems([...selectedItems, idStr]);
    }
  };

  // 검색 파라미터 동기화 헬퍼 함수
  const syncSearchParams = (page: number = currentPage) => {
    updateSearchParams({
      brand: brandInput,
      storeType: storeTypeInput,
      status: statusInput,
      startDate: startDateInput,
      endDate: endDateInput,
      groupChannel: groupChannelInput,
      page,
    });
  };

  // 검색 버튼 핸들러
  const handleSearch = () => {
    syncSearchParams(1);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    updateSearchParams({
      brand: selectedBrand,
      storeType: selectedStoreType,
      status: selectedStatus,
      startDate: startDate,
      endDate: endDate,
      groupChannel: selectedGroupChannel,
      page,
    });
  };

  // 초기화 버튼 핸들러
  const handleReset = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  // 금액 포맷
  const formatAmount = (amount: number) => {
    return amount.toLocaleString("ko-KR");
  };

  // 상태에 따른 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case "완료":
        return "#28a745";
      case "A/S진행":
        return "#007bff";
      case "견적진행":
        return "#17a2b8";
      case "업체배정":
        return "#6610f2";
      case "접수중":
        return "#ffc107";
      case "취소":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  // 엑셀 다운로드 함수
  const handleExcelDownload = () => {
    const baseUrl = process.env.REACT_APP_SERVER_IP || "http://localhost:3000";
    const token = getCookie("ac_token");

    if (!token) {
      alert('인증 정보가 없습니다. 다시 로그인해주세요.');
      return;
    }

    // 쿼리 파라미터 구성
    const excelQueryParams: Record<string, string> = {
      "filter.insert_date": `$btw:${startDate},${endDate}`,
      sortBy: "insert_date:DESC",
    };

    if (selectedBrand) {
      excelQueryParams["filter.store_user.brand"] = `$in:${selectedBrand}`;
    }
    if (selectedStoreType) {
      excelQueryParams["filter.store_type"] = `$in:${selectedStoreType}`;
    }
    if (selectedStatus) {
      excelQueryParams["filter.search_progress"] = `$in:${selectedStatus}`;
    }
    if (selectedGroupChannel) {
      excelQueryParams["filter.group_part_idx"] = `$in:${selectedGroupChannel}`;
    }

    // 쿼리 스트링 생성
    const queryString = new URLSearchParams(excelQueryParams).toString();
    const downloadUrl = `${baseUrl}/as/search/excel?${queryString}`;

    // 파일 다운로드 처리
    fetch(downloadUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('엑셀 다운로드 실패');
      }
      return response.blob();
    })
    .then(blob => {
      // 다운로드 링크 생성 및 클릭
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AS_리스트_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(error => {
      console.error('Excel download error:', error);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    });
  };

  return (
    <>
      <header className={cn({ hasAlarm: hasAlarm })}>
        <div>
          <Button variant="text" className="btn-back no-ico">
            종합 보수 리스트
          </Button>
        </div>
        <div className="btn-box">
          <AlarmButton />
          <IconButton
            btnName="마이페이지"
            className="user"
            onClick={() => navigate("/profile/" + userInfo.user_id)}
          >
            <SVG name="userBlack" />
          </IconButton>
        </div>
      </header>

      <Body isTabbar={true}>
        <Container>
          <FilterSection>
            <FilterGroup>
              <select value={brandInput} onChange={(e) => setBrandInput(e.target.value)}>
                <option value="">전체 브랜드</option>
                <option value="M">MLB</option>
                <option value="I">MLB KIDS</option>
                <option value="X">Discovery</option>
                <option value="V">DUVETICA</option>
              </select>
              <select value={storeTypeInput} onChange={(e) => setStoreTypeInput(e.target.value)}>
                <option value="">전체 매장유형</option>
                <option value="A">백화점</option>
                <option value="B">대리점</option>
                <option value="C">직영점</option>
                <option value="D">면세점</option>
              </select>
              <select value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
                <option value="">전체 상태</option>
                <option value="as_20">접수중</option>
                <option value="as_40">업체배정</option>
                <option value="as_60">견적진행</option>
                <option value="as_80">A/S진행</option>
                <option value="as_100">완료</option>
                <option value="as_0">취소</option>
              </select>
              <select value={groupChannelInput} onChange={(e) => setGroupChannelInput(e.target.value)}>
                <option value="">전체 그룹채널</option>
                {groupChannelList.map((group: GroupChannel) => (
                  <option key={group.group_part_idx} value={group.group_part_idx}>
                    {group.group_name}
                  </option>
                ))}
              </select>
              <input
                type="date"
                placeholder="시작일"
                value={startDateInput}
                onChange={(e) => setStartDateInput(e.target.value)}
              />
              <input
                type="date"
                placeholder="종료일"
                value={endDateInput}
                onChange={(e) => setEndDateInput(e.target.value)}
              />
            </FilterGroup>
            <ButtonGroup>
              <Button variant="outlined" size="small" onClick={handleReset}>
                초기화
              </Button>
              <Button variant="contained" size="small" onClick={handleSearch}>
                검색
              </Button>
            </ButtonGroup>
          </FilterSection>

          <TableSection>
            <TableHeader>
              <h3>검색 결과 ({totalItems}건)</h3>
              <ExportButton onClick={handleExcelDownload}>
                <SVG name="fileDownload" width={16} height={16} />
                엑셀 다운로드
              </ExportButton>
            </TableHeader>

            {isLoading ? (
              <LoadingContainer>
                <p>데이터를 불러오는 중...</p>
              </LoadingContainer>
            ) : error ? (
              <LoadingContainer>
                <p style={{ color: '#dc3545' }}>데이터를 불러오는 중 오류가 발생했습니다.</p>
              </LoadingContainer>
            ) : (
              <>
                {isMobile ? (
                  <MobileCardList>
                    {repairData.map((item) => (
                      <MobileCard
                        key={item.id}
                        onClick={() => navigate(`/as-detail/${item.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <CardHeader>
                          <div onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(String(item.id))}
                              onChange={() => handleSelectItem(item.id)}
                            />
                          </div>
                          <StoreName>{item.storeName}</StoreName>
                          <StatusBadge color={getStatusColor(item.status)}>
                            {item.status}
                          </StatusBadge>
                        </CardHeader>
                        <CardBody>
                          <CardRow>
                            <CardLabel>브랜드</CardLabel>
                            <CardValue>{item.brand}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>매장유형</CardLabel>
                            <CardValue>{item.storeType}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>요청내용</CardLabel>
                            <CardValue className="request-content">{item.requestContent}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>접수일자</CardLabel>
                            <CardValue>{item.requestDate}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>업체선정일자</CardLabel>
                            <CardValue>{item.companyAssignedDate}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>완료일자</CardLabel>
                            <CardValue>{item.completeDate}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>업체명</CardLabel>
                            <CardValue>{item.companyName}</CardValue>
                          </CardRow>
                          <CardRow>
                            <CardLabel>금액</CardLabel>
                            <CardValue>{item.amount > 0 ? item.amount.toLocaleString() + "원" : "-"}</CardValue>
                          </CardRow>
                        </CardBody>
                      </MobileCard>
                    ))}
                  </MobileCardList>
                ) : (
                  <TableWrapper>
                    <StyledTable>
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={selectedItems.length === repairData.length && repairData.length > 0}
                            />
                          </th>
                          <th>브랜드</th>
                          <th>매장유형</th>
                          <th>매장명</th>
                          <th>요청내용</th>
                          <th>접수일자</th>
                          <th>업체선정일자</th>
                          <th>완료일자</th>
                          <th>업체명</th>
                          <th>금액</th>
                          <th>상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repairData.map((item) => (
                          <tr
                            key={item.id}
                            onClick={() => navigate(`/as-detail/${item.id}`)}
                            style={{ cursor: "pointer" }}
                          >
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(String(item.id))}
                                onChange={() => handleSelectItem(item.id)}
                              />
                            </td>
                            <td>{item.brand}</td>
                            <td>{item.storeType}</td>
                            <td>{item.storeName}</td>
                            <td className="request-content">{item.requestContent}</td>
                            <td>{item.requestDate}</td>
                            <td>{item.companyAssignedDate}</td>
                            <td>{item.completeDate}</td>
                            <td>{item.companyName}</td>
                            <td>{item.amount > 0 ? item.amount.toLocaleString() + "원" : "-"}</td>
                            <td>
                              <StatusBadge color={getStatusColor(item.status)}>
                                {item.status}
                              </StatusBadge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </StyledTable>
                  </TableWrapper>
                )}

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                  <Pagination>
                    <PageButton
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      &laquo;
                    </PageButton>
                    <PageButton
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      &lt;
                    </PageButton>

                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      const endPage = Math.min(totalPages, Math.max(1, currentPage - Math.floor(maxVisiblePages / 2)) + maxVisiblePages - 1);
                      const startPage = Math.max(1, endPage - maxVisiblePages + 1);

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <PageNumber
                            key={i}
                            $active={i === currentPage}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </PageNumber>
                        );
                      }
                      return pages;
                    })()}

                    <PageButton
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      &gt;
                    </PageButton>
                    <PageButton
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      &raquo;
                    </PageButton>
                  </Pagination>
                )}
              </>
            )}
          </TableSection>
        </Container>
      </Body>
    </>
  );
};

// Styled Components
const Container = styled.div`
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FilterSection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  select,
  input {
    flex: 1;
    min-width: 150px;
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 14px;

    @media (max-width: 768px) {
      min-width: 100%;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;

    button {
      flex: 1;
    }
  }
`;

const TableSection = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #dee2e6;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #212529;
  }
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: #f8f9fa;

    th {
      padding: 12px;
      text-align: left;
      font-weight: 600;
      font-size: 14px;
      color: #495057;
      border-bottom: 2px solid #dee2e6;
      white-space: nowrap;

      &:first-child {
        ${/* 체크박스 */ ""}
        width: 3%;
        text-align: center;
      }

      &:nth-child(2) {
        ${/* 브랜드 컬럼 */ ""}
        width: 7%;
      }

      &:nth-child(3) {
        ${/* 매장유형 컬럼 */ ""}
        width: 6%;
      }

      &:nth-child(4) {
        ${/* 매장명 컬럼 */ ""}
        width: 8%;
      }

      &:nth-child(5) {
        ${/* 요청내용 컬럼 */ ""}
        width: 28%;
      }

      &:nth-child(6) {
        ${/* 접수일자 컬럼 */ ""}
        width: 8%;
      }

      &:nth-child(7) {
        ${/* 업체선정일자 컬럼 */ ""}
        width: 9%;
      }

      &:nth-child(8) {
        ${/* 완료일자 컬럼 */ ""}
        width: 8%;
      }

      &:nth-child(9) {
        ${/* 업체명 컬럼 */ ""}
        width: 8%;
      }

      &:nth-child(10) {
        ${/* 금액 컬럼 */ ""}
        width: 9%;
      }

      &:nth-child(11) {
        ${/* 상태 컬럼 */ ""}
        width: 6%;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #dee2e6;
      transition: background 0.2s;

      &:hover {
        background: #f8f9fa;
      }
    }

    td {
      padding: 12px;
      font-size: 14px;
      color: #212529;

      &:first-child {
        text-align: center;
      }

      &.request-content {
        white-space: pre-wrap;
        word-break: break-word;
      }
    }
  }

  tfoot {
    background: #f8f9fa;
    font-weight: 600;

    td {
      padding: 12px;
      font-size: 14px;
      color: #212529;
      border-top: 2px solid #dee2e6;
    }
  }
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 8px;
  background: ${({ color }) => color};
  color: white;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

// Mobile Styles
const MobileCardList = styled.div`
  padding: 16px;
`;

const MobileCard = styled.div`
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
  }
`;

const StoreName = styled.div`
  flex: 1;
  font-weight: 600;
  font-size: 15px;
  color: #212529;
`;

const CardBody = styled.div`
  padding: 12px;
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;

  &:not(:last-child) {
    border-bottom: 1px solid #f1f3f5;
  }
`;

const CardLabel = styled.span`
  color: #6c757d;
  font-weight: 500;
`;

const CardValue = styled.span`
  color: #212529;
  text-align: right;
  flex: 1;
  max-width: 60%;

  &.request-content {
    white-space: pre-wrap;
    word-break: break-word;
    text-align: left;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;

  p {
    font-size: 16px;
    color: #6c757d;
  }
`;

// 페이지네이션 스타일
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 20px;
  border-top: 1px solid #dee2e6;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: ${({ disabled }) => (disabled ? "#f8f9fa" : "#fff")};
  color: ${({ disabled }) => (disabled ? "#adb5bd" : "#495057")};
  font-size: 14px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e9ecef;
    border-color: #adb5bd;
  }
`;

const PageNumber = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.color.main : "#dee2e6")};
  border-radius: 4px;
  background: ${({ $active, theme }) => ($active ? theme.color.main : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#495057")};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $active, theme }) => ($active ? theme.color.main : "#e9ecef")};
    border-color: ${({ $active, theme }) => ($active ? theme.color.main : "#adb5bd")};
  }
`;

export default RepairList;